import { EntityName, PROVIDER } from './constants.js';
import queryString from 'query-string';
import { createEntity, arrayOf, shape, string, number, boolean, compose, schema, date, ENTITY_DEFINED_LATER } from '@dtvgo/typed-normalizr';
import { VrioContentType } from '@dtvgo/constants';
import { WithImages, Team, ImagesCollection, MatchTeam } from './interfaces.js';
import { normalizeGenres, addRatioToImages, convertImagesArrayToObject, completeChildrenProps, sortChildren, typeToEntityName, convertChildrenArrayToObject, convertImageUrlToImageCollection, genericConvertImagesArrayToObject } from './utils.js';
import 'lodash/set';
import 'lodash/sortBy';
import 'lodash/mapKeys';
import 'lodash/defaultsDeep';

const ContentEntity = createEntity(EntityName.CONTENT, {
  channels: arrayOf(shape({
    id: string(),
    name: string()
  })),
  sourceProvider: string(PROVIDER.VRIO),
  vrioAssetId: string(),
  type: string(),
  title: string(),
  description: string(),
  vmsId: string(),
  duration: number(),
  episode: shape({
    number: number(),
    seasonNumber: number(),
    showId: string(),
    showName: string()
  }),
  rating: string(),
  ratingWarning: arrayOf(string()),
  ratingDescription: string(),
  genres: arrayOf(string()),
  releaseYear: number(),
  labels: arrayOf(string()),
  progress: number(),
  lockIcon: boolean(),
  shouldDisplayLockIcon: boolean(),
  ...WithImages,
  metadata: shape({
    id: string()
  }),
  moreLikeThis: [],
  interactivities: shape({
    bets: boolean()
  })
}, {
  idAttribute: (content) => content.vrioAssetId,
  preDeserialize: compose(
    (content) => ({
      ...content,
      // Extract fields inside vod
      releaseYear: content.releaseYear || content.vod?.releaseYear,
      duration: content.duration || content.vod?.duration,
      progress: content.vod?.progress,
      // Normalize type of content
      type: content.type?.toUpperCase()
    }),
    normalizeGenres,
    addRatioToImages,
    convertImagesArrayToObject
  )
});
const SeasonEntity = createEntity(EntityName.SEASON, {
  title: string(),
  vmsId: string(),
  seasonNumber: number(),
  sourceProvider: string(PROVIDER.VRIO),
  showId: string(),
  showName: string(),
  hasEpisodes: boolean(),
  episodes: [ContentEntity]
}, {
  idAttribute(value) {
    return `${value.showId}_${value.seasonNumber}`;
  },
  preDeserialize: compose(
    // Complete episode props
    completeChildrenProps({
      sourceProvider: "sourceProvider",
      showId: "episode.showId",
      seasonNumber: "episode.seasonNumber",
      showName: "episode.showName"
    })("episodes"),
    // Sort episodes by number
    sortChildren("episodes", "episode.number")
  )
});
const ShowEntity = createEntity(EntityName.SHOW, {
  sourceProvider: string(PROVIDER.VRIO),
  vrioAssetId: string(),
  title: string(),
  description: string(),
  rating: string(),
  ratingWarning: arrayOf(string()),
  ratingDescription: string(),
  releaseYear: number(),
  lockIcon: boolean(),
  shouldDisplayLockIcon: boolean(),
  genres: arrayOf(string()),
  ...WithImages,
  seasons: [SeasonEntity],
  moreLikeThis: []
}, {
  idAttribute: (show) => show.vrioAssetId,
  preDeserialize: compose(
    // Add missing attributes to seasons
    completeChildrenProps({
      sourceProvider: "sourceProvider",
      vrioAssetId: "showId",
      title: "showName"
    })("seasons"),
    normalizeGenres,
    addRatioToImages,
    convertImagesArrayToObject,
    // Sort seasons
    sortChildren("seasons", "seasonNumber"),
    (entity) => ({
      ...entity,
      releaseYear: entity?.vod?.releaseYear
    })
  )
});
const moreLikeThis = new schema.Array({
  [EntityName.SHOW]: ShowEntity,
  [EntityName.CONTENT]: ContentEntity
}, (value) => typeToEntityName(value.type));
ShowEntity.define({ moreLikeThis });
ContentEntity.define({ moreLikeThis });
const ScheduleEntity = createEntity(EntityName.SCHEDULE, {
  sourceProvider: string(PROVIDER.VRIO),
  guideId: string(),
  channelId: string(),
  channelName: string(),
  blackout: boolean(),
  startTime: date(),
  endTime: date(),
  qualifiers: arrayOf(string()),
  interactivities: shape({
    bets: boolean()
  }),
  sport: shape({
    pageType: string(),
    championship: string(),
    sportIds: shape({
      optaGroupId: string(),
      fifaGroupId: string(),
      optaStageId: string(),
      optaMatchId: string(),
      fifaMatchId: string(),
      fifaEventId: string()
    }),
    teams: arrayOf(Team)
  }),
  content: ContentEntity,
  channel: ENTITY_DEFINED_LATER
}, {
  idAttribute: (schedule) => {
    const channelId = schedule.channelId || schedule.live.channelId;
    const startTime = schedule.startTime || schedule.live.startTime;
    return `${channelId}_${new Date(startTime).getTime()}`;
  },
  preDeserialize: compose(
    // Split schedule and content
    ({
      sourceProvider,
      live,
      guideId,
      sport,
      interactivities,
      ...content
    }) => ({
      sourceProvider,
      guideId,
      sport,
      interactivities,
      ...live,
      content: {
        sourceProvider,
        interactivities,
        ...content
      }
    }),
    // If this schedule is not already processed by channel, add a channel value
    // to have the channel with this schedule in the normalized entities
    (schedule) => schedule.fromChannel ? schedule : {
      ...schedule,
      channel: {
        channelId: schedule.channelId,
        schedules: [{ ...schedule }]
      }
    }
  )
});
const ChannelEntity = createEntity(EntityName.CHANNEL, {
  channelId: string(),
  title: string(),
  ...WithImages,
  vmsId: string(),
  sourceProvider: string(PROVIDER.VRIO),
  assetToken: string(),
  schedules: new schema.Values(ScheduleEntity),
  categories: arrayOf(string()),
  isEntitled: boolean()
}, {
  idAttribute: (channel) => channel.channelId,
  preDeserialize: compose(
    completeChildrenProps({
      sourceProvider: "sourceProvider",
      channelId: "live.channelId",
      title: "live.channelName"
    })("schedules"),
    completeChildrenProps({
      channelId: "live.fromChannel"
    })("schedules"),
    addRatioToImages,
    convertImagesArrayToObject,
    convertChildrenArrayToObject(
      "schedules",
      (schedule) => `${new Date(schedule.startTime || schedule.live?.startTime).getTime()}_${new Date(schedule.endTime || schedule.live?.endTime).getTime()}`
    )
  ),
  mergeStrategy: (channelA, channelB) => ({
    ...channelA,
    ...channelB,
    schedules: {
      ...channelA.schedules,
      ...channelB.schedules
    }
  })
});
ScheduleEntity.define({ channel: ChannelEntity });
const CarouselEntity = createEntity(EntityName.CAROUSEL, {
  sourceProvider: string(PROVIDER.VRIO),
  sourceCarouselId: string(),
  source: string(),
  type: string(),
  title: string(),
  filters: shape({
    groupId: string(),
    teamId: string()
  }),
  contents: new schema.Array({
    [EntityName.CONTENT]: ContentEntity,
    [EntityName.SHOW]: ShowEntity,
    [EntityName.CHANNEL]: ChannelEntity,
    [EntityName.SCHEDULE]: ScheduleEntity
  }, (value) => {
    if (value.isLive && value.live && value.type !== VrioContentType.CHANNEL) {
      return EntityName.SCHEDULE;
    }
    return typeToEntityName(value.type);
  }),
  collections: arrayOf(
    shape(
      {
        title: string(),
        headline: string(),
        subtitle: string(),
        description: string(),
        buttonText: string(),
        cardImages: ImagesCollection,
        iconImages: ImagesCollection,
        backGroundImages: ImagesCollection,
        sportIds: shape({
          optaTeamId: string(),
          fifaTeamId: string(),
          optaGroupId: string(),
          fifaGroupId: string()
        }),
        actions: arrayOf(
          shape({
            target: string(),
            text: string(),
            type: string()
          })
        )
      },
      // Predeserialize each collection
      compose(
        convertImageUrlToImageCollection("cardImage", "cardImages"),
        convertImageUrlToImageCollection("backGroundImage", "backGroundImages"),
        genericConvertImagesArrayToObject("cardImages"),
        genericConvertImagesArrayToObject("iconImages"),
        genericConvertImagesArrayToObject("backGroundImages")
      )
    )
  ),
  sport: shape({
    standing: shape({
      groups: arrayOf(shape({
        optaGroupId: string(),
        fifaGroupId: string(),
        title: string(),
        completed: boolean(),
        teams: arrayOf(shape({
          team: Team,
          ranking: number(),
          scoreDifference: number(),
          points: number(),
          isRanked: boolean()
        }))
      })),
      playoffStages: arrayOf(shape({
        title: string(),
        isCurrentStage: boolean(),
        optaStageId: string(),
        fifaStageId: string(),
        matches: arrayOf(shape({
          matchId: string(),
          home: MatchTeam,
          away: MatchTeam
        }))
      }))
    }),
    contentFilter: shape({
      type: string(),
      filters: arrayOf(
        shape(
          {
            images: ImagesCollection,
            optaTeamId: string(),
            fifaTeamId: string(),
            optaGroupId: string(),
            fifaGroupId: string(),
            optaStageId: string(),
            fifaStageId: string(),
            name: string()
          },
          // Predeserialize each filter
          convertImagesArrayToObject
        )
      )
    }),
    match: shape({
      optaMatchId: string(),
      fifaMatchId: string()
    })
  })
}, {
  idAttribute: (carousel) => {
    const sourceCarouselElementId = carousel?.sourceCarouselId || carousel?.elementId;
    const serializedFilters = queryString.stringify(
      carousel.filters,
      { skipEmptyString: true, skipNull: true }
    );
    return serializedFilters ? `${sourceCarouselElementId || carousel.carouselId}?${serializedFilters}` : sourceCarouselElementId || carousel.carouselId;
  },
  preDeserialize: compose(
    completeChildrenProps({ sourceProvider: "sourceProvider" })("contents"),
    (carousel) => ({
      ...carousel,
      contents: carousel.contents?.filter(
        (content) => content.type === VrioContentType.CHANNEL && !!content.channelId || !!content.vrioAssetId || !!content.id
      )
    })
  )
});
const PageEntity = createEntity(EntityName.PAGE, {
  id: string(),
  sourceProvider: string(PROVIDER.VRIO),
  carousels: arrayOf(
    shape(
      {
        sourceProvider: string(PROVIDER.VRIO),
        source: string(),
        sourceCarouselId: string(),
        type: string(),
        filters: shape({
          teamId: string(),
          groupId: string()
        }),
        tab: shape({
          id: string(),
          title: string()
        })
      }
    )
  ),
  sportIds: shape({
    optaTeamId: string(),
    fifaTeamId: string(),
    optaGroupId: string(),
    fifaGroupId: string(),
    optaStageId: string(),
    fifaEventId: string(),
    optaMatchId: string(),
    fifaMatchId: string()
  })
}, {
  idAttribute: (page) => {
    const serializedSportIds = queryString.stringify(
      page.sportIds,
      { skipEmptyString: true, skipNull: true }
    );
    return serializedSportIds ? `${page.id}?${serializedSportIds}` : page.id;
  },
  preDeserialize: compose(
    completeChildrenProps({ sourceProvider: "sourceProvider" })("carousels")
  )
});
const PageEntityV4 = createEntity(EntityName.PAGE, {
  id: string(),
  sourceProvider: string(PROVIDER.VRIO),
  elements: arrayOf(
    shape(
      {
        sourceProvider: string(PROVIDER.VRIO),
        source: string(),
        elementId: string(),
        type: string(),
        filters: shape({
          teamId: string(),
          groupId: string(),
          matchId: string()
        }),
        tab: shape({
          id: string(),
          title: string()
        }),
        provider: string(),
        title: string()
      }
    )
  ),
  sportIds: shape({
    optaTeamId: string(),
    fifaTeamId: string(),
    optaGroupId: string(),
    fifaGroupId: string(),
    optaStageId: string(),
    fifaEventId: string(),
    optaMatchId: string(),
    fifaMatchId: string()
  })
}, {
  idAttribute: (page) => {
    const serializedSportIds = queryString.stringify(
      page.sportIds,
      { skipEmptyString: true, skipNull: true }
    );
    return serializedSportIds ? `${page.id}?${serializedSportIds}` : page.id;
  },
  preDeserialize: compose(
    completeChildrenProps({ sourceProvider: "sourceProvider" })("carousels")
  )
});

export { CarouselEntity, ChannelEntity, ContentEntity, PageEntity, PageEntityV4, ScheduleEntity, SeasonEntity, ShowEntity };
//# sourceMappingURL=entities.js.map
